import { CALIBRATION_TYPE, CalibrationType } from '../../config/calibration';
import toast from 'react-hot-toast';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, ChevronLeft, ChevronRight, ClipboardPaste, Loader2 } from 'lucide-react';
import { DEFAULT_CLIPBOARD_FILL_LABEL, FormField, FormSchema, Option } from './types';
import { formBuilderApi } from './api';
import { matchesCondition, resolveFieldVisibility } from './conditions';
import FieldControl from './renderer/FieldControl';
import ReviewView from './renderer/ReviewView';
import SubmissionErrorBanner from './renderer/SubmissionErrorBanner';
import { formatSubmissionError, getApiErrorCode, getApiErrorField, getDataSourceError } from './renderer/errors';
import { clientValidate, sameFieldDefinitions, sameValue } from './renderer/validate';
import { parseClipboard, planClipboardFill } from './renderer/clipboardFill';


interface Props {
  schema: FormSchema;
  value?: Record<string, unknown>;
  onChange?: (values: Record<string, unknown>) => void;
  onSubmit?: (values: Record<string, unknown>) => void | Promise<void>;
  readOnly?: boolean;
  showStepNavigation?: boolean;
  runtimeFormId?: string;
  /** true resolves against the saved draft; a schema resolves against that unsaved schema (editor preview). */
  dataSourceDraft?: boolean | FormSchema;
  initialStep?: number;
  onStepChange?: (step: number) => void;
  /** Values applied to runtime-generated fields (step dynamic schema / dynamic groups) the first time a field with that key appears. Never creates keys on its own. */
  dynamicSeed?: Record<string, unknown>;
  /** Sent with the runtime submit so the API can treat it as a re-calibration. */
  calibrationType?: CalibrationType;
}

export default function FormRenderer({ schema, value, onChange, onSubmit, readOnly, showStepNavigation = true, runtimeFormId, dataSourceDraft = false, initialStep = 0, onStepChange, dynamicSeed, calibrationType }: Props) {
  const defaults = useMemo(() => {
    const out: Record<string, unknown> = { ...(value || {}) };
    schema.steps.forEach((s) => s.fields.forEach((f) => { if (out[f.key] === undefined && f.default_value !== undefined) out[f.key] = f.default_value; }));
    return out;
  }, [schema, value]);
  const [values, setValues] = useState(defaults);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [isSubmissionErrorModalOpen, setIsSubmissionErrorModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pendingErrorField, setPendingErrorField] = useState<string | null>(null);
  const [step, setStep] = useState(() => Math.max(0, Math.min(initialStep, Math.max(schema.steps.length, 1))));
  const [resolvedOptions, setResolvedOptions] = useState<Record<string, Option[]>>({});
  const [resolvedDynamicFields, setResolvedDynamicFields] = useState<Record<string, FormField[]>>({});
  const resolvedDynamicFieldsRef = useRef(resolvedDynamicFields);
  resolvedDynamicFieldsRef.current = resolvedDynamicFields;
  const [resolvedStepDynamicFields, setResolvedStepDynamicFields] = useState<Record<string, FormField[]>>({});
  const [stepDynamicLoading, setStepDynamicLoading] = useState<Record<string, boolean>>({});
  const [stepDynamicErrors, setStepDynamicErrors] = useState<Record<string, string | undefined>>({});
  const resolvedStepDynamicFieldsRef = useRef(resolvedStepDynamicFields);
  resolvedStepDynamicFieldsRef.current = resolvedStepDynamicFields;
  const fieldNodes = useRef<Record<string, HTMLElement | null>>({});
  const submissionErrorNode = useRef<HTMLDivElement | null>(null);
  // Only the explicit Submit button is allowed to invoke the submission flow.
  // This prevents browser implicit-submit behaviour (for example Enter on an input)
  // from submitting while the user is merely entering/reviewing the form.
  const submitIntentRef = useRef(false);

  const changeStep = (next: number | ((current: number) => number)) => {
    setStep((current) => {
      const resolved = typeof next === 'function' ? next(current) : next;
      const bounded = Math.max(0, Math.min(resolved, Math.max(schema.steps.length, 1)));
      if (bounded !== current) onStepChange?.(bounded);
      return bounded;
    });
  };

  const setValue = (key: string, v: unknown) => setValues((prev) => {
    const next = { ...prev, [key]: v };
    if (!sameValue(prev[key], v)) {
      schema.steps.forEach((s) => s.fields.forEach((sourceField) => {
        (sourceField.reset_rules || []).forEach((rule) => {
          const triggerKey = rule.when?.field;
          const shouldReset = rule.when?.operator === 'changed' ? triggerKey === key : !!rule.when && matchesCondition(rule.when, next);
          if (shouldReset) rule.fields.forEach((target) => { if (target !== key) next[target] = undefined; });
        });
      }));
    }
    onChange?.(next);
    return next;
  });

  const allFields = useMemo(() => schema.steps.flatMap((s) => s.fields), [schema]);

  const handleDynamicFieldsChange = (groupKey: string, fields: FormField[]) => {
    const previousFields = resolvedDynamicFieldsRef.current[groupKey] || [];
    if (previousFields.length === 0 && fields.length === 0) return;

    const definitionsUnchanged = sameFieldDefinitions(previousFields, fields);
    setResolvedDynamicFields((current) => ({ ...current, [groupKey]: fields }));

    // Re-mounting a dynamic group can report its cached definition before any
    // request runs. If the definition is unchanged, keep all entered values/errors.
    if (definitionsUnchanged) return;

    setErrors((current) => {
      const next = { ...current };
      const nextKeys = new Set(fields.map((field) => field.key));
      previousFields.forEach((field) => {
        if (!nextKeys.has(field.key)) delete next[field.key];
      });
      return next;
    });

    setValues((current) => {
      const next = { ...current };
      const nextKeys = new Set(fields.map((field) => field.key));
      previousFields.forEach((field) => {
        if (!nextKeys.has(field.key)) delete next[field.key];
      });
      fields.forEach((field) => {
        if (next[field.key] === undefined && dynamicSeed?.[field.key] !== undefined) next[field.key] = dynamicSeed[field.key];
        if (field.default_value !== undefined && next[field.key] === undefined) {
          next[field.key] = field.default_value;
        }
      });
      onChange?.(next);
      return next;
    });
  };

  const fieldVisibility = useMemo(() => resolveFieldVisibility(allFields, values), [allFields, values]);
  const isVisible = (f: FormField) => fieldVisibility[f.key] !== false;
  const visibleKeys = useMemo(() => new Set(allFields.filter(isVisible).map((f) => f.key)), [allFields, fieldVisibility]);

  const activeStepDefinition = schema.steps[step];
  const runtimeDynamicSource = activeStepDefinition?.dynamic_schema;
  const runtimeDynamicDependencies = runtimeDynamicSource?.trigger.dependency_mode === 'selected'
    ? (runtimeDynamicSource.trigger.trigger_fields || [])
    : (runtimeDynamicSource?.trigger.depends_on || []);
  const runtimeDynamicDependencyValues = runtimeDynamicDependencies.map((key) => values[key]);

  useEffect(() => {
    if (!runtimeFormId || !activeStepDefinition || !runtimeDynamicSource || runtimeDynamicSource.trigger.type === 'manual') return;
    if (runtimeDynamicSource.trigger.type === 'form_load' && step !== 0) return;

    let cancelled = false;
    const resolve = async () => {
      setStepDynamicLoading((current) => ({ ...current, [activeStepDefinition.id]: true }));
      setStepDynamicErrors((current) => ({ ...current, [activeStepDefinition.id]: undefined }));
      try {
        const result = await formBuilderApi.resolveStepDynamicSchema(runtimeFormId, activeStepDefinition.id, values, dataSourceDraft);
        if (cancelled) return;
        const fields = (result.fields || []).map((field, index) => ({ ...field, order: field.order ?? index, validations: field.validations || [], options: field.options || [], reset_rules: field.reset_rules || [], accept: field.accept || [], multiple: !!field.multiple }));
        const previous = resolvedStepDynamicFieldsRef.current[activeStepDefinition.id] || [];
        setResolvedStepDynamicFields((current) => ({ ...current, [activeStepDefinition.id]: fields }));
        setValues((current) => {
          const next = { ...current };
          const nextKeys = new Set(fields.map((field) => field.key));
          previous.forEach((field) => { if (!nextKeys.has(field.key)) delete next[field.key]; });
          fields.forEach((field) => {
            if (next[field.key] === undefined && dynamicSeed?.[field.key] !== undefined) next[field.key] = dynamicSeed[field.key];
            if (field.default_value !== undefined && next[field.key] === undefined) next[field.key] = field.default_value;
          });
          onChange?.(next);
          return next;
        });
      } catch (error) {
        if (!cancelled) {
          setResolvedStepDynamicFields((current) => ({ ...current, [activeStepDefinition.id]: [] }));
          setStepDynamicErrors((current) => ({ ...current, [activeStepDefinition.id]: getDataSourceError(error) }));
        }
      } finally {
        if (!cancelled) setStepDynamicLoading((current) => ({ ...current, [activeStepDefinition.id]: false }));
      }
    };

    const trigger = runtimeDynamicSource.trigger.type;
    const delay = trigger === 'dependency_change' || trigger === 'step_enter_and_dependency_change' ? runtimeDynamicSource.trigger.debounce_ms : 0;
    const timer = window.setTimeout(resolve, delay);
    return () => { cancelled = true; window.clearTimeout(timer); };
  // Dependency values are deliberately the only value inputs that trigger a runtime schema request.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeFormId, dataSourceDraft, step, activeStepDefinition?.id, runtimeDynamicSource, ...runtimeDynamicDependencyValues]);

  useEffect(() => {
    const hiddenGroupKeys = allFields.filter((field) => field.type === 'dynamic_group' && fieldVisibility[field.key] === false).map((field) => field.key);
    if (!hiddenGroupKeys.length) return;
    setValues((current) => {
      const next = { ...current };
      let changed = false;
      hiddenGroupKeys.forEach((key) => (resolvedDynamicFields[key] || []).forEach((field) => {
        if (Object.prototype.hasOwnProperty.call(next, field.key)) { delete next[field.key]; changed = true; }
      }));
      if (changed) onChange?.(next);
      return changed ? next : current;
    });
  }, [allFields, fieldVisibility, resolvedDynamicFields]);
  const currentRuntimeFields = resolvedStepDynamicFields[activeStepDefinition?.id || ''] || [];
  const visibleRuntimeFields = currentRuntimeFields.filter((field) => field.visible_when ? matchesCondition(field.visible_when, values, fieldVisibility) : true);
  const visibleFields = [
    ...(schema.steps[step]?.fields.filter(isVisible) || []),
    ...visibleRuntimeFields,
  ];
  const expandDynamicFields = (fields: FormField[]) => fields.flatMap((f) => f.type === 'dynamic_group' ? [f, ...(resolvedDynamicFields[f.key] || [])] : [f]);
  const allCachedRuntimeFields = Object.values(resolvedStepDynamicFields).flat();
  const allVisibleFields = expandDynamicFields(schema.steps.reduce<FormField[]>((all, s) => all.concat(s.fields.filter(isVisible)), []).concat(allCachedRuntimeFields));
  const visibleValidationFields = expandDynamicFields(visibleFields);
  // Every form ends with a review step; show_in_review decides which fields appear on it.
  const reviewStepIndex = schema.steps.length;
  const onReviewStep = step === reviewStepIndex;
  // Re-calibration: step 1 (sensor details) is prefilled from the existing sensor and must not be edited.
  const lockFirstStep = calibrationType === CALIBRATION_TYPE.RECALIBRATION && step === 0;
  const totalStepCount = schema.steps.length + 1;

  const reviewFieldsByStep = useMemo(() => {
    const groups = schema.steps.map((stepItem) => {
      const fields: FormField[] = [];
      stepItem.fields.forEach((sourceField) => {
        if (!isVisible(sourceField) || sourceField.show_in_review === false) return;
        if (sourceField.type === 'dynamic_group') {
          const configs = sourceField.data_source?.response.dynamic_field_configs || {};
          (resolvedDynamicFields[sourceField.key] || []).forEach((generatedField) => {
            const show = configs[generatedField.key]?.show_in_review ?? generatedField.show_in_review ?? true;
            if (show && isVisible(generatedField)) fields.push(generatedField);
          });
        } else {
          fields.push(sourceField);
        }
      });
      (resolvedStepDynamicFields[stepItem.id] || []).forEach((runtimeField) => {
        if (runtimeField.show_in_review !== false && isVisible(runtimeField)) fields.push(runtimeField);
      });
      return { step: stepItem, fields };
    });
    return groups.filter(({ fields }) => fields.length > 0);
  }, [schema, isVisible, resolvedDynamicFields, resolvedStepDynamicFields]);

  const reviewFields = useMemo(
    () => reviewFieldsByStep.flatMap(({ fields }) => fields),
    [reviewFieldsByStep],
  );

  const validate = (fields: FormField[]) => {
    const next: Record<string, string> = {};
    fields.forEach((f) => { const e = clientValidate(f, values[f.key], values); if (e) next[f.key] = e; });
    setErrors((prev) => {
      const merged = { ...prev };
      fields.forEach((f) => { if (next[f.key]) merged[f.key] = next[f.key]; else delete merged[f.key]; });
      return merged;
    });
    return next;
  };

  const focusFirstError = (next: Record<string, string>) => {
    const key = Object.keys(next)[0];
    if (key) requestAnimationFrame(() => { fieldNodes.current[key]?.focus?.(); fieldNodes.current[key]?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }); });
  };

  // API field errors may belong to a different step or a dynamically
  // resolved field. Keep checking briefly until React has actually mounted
  // the target instead of relying on a single animation frame.
  useEffect(() => {
    if (!pendingErrorField) return;
    let cancelled = false;
    let attempts = 0;
    let frame = 0;
    const locate = () => {
      if (cancelled) return;
      const node = fieldNodes.current[pendingErrorField];
      if (node) {
        node.focus?.();
        node.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        setPendingErrorField(null);
        return;
      }
      attempts += 1;
      if (attempts < 20) frame = window.requestAnimationFrame(locate);
      // Never found: stop waiting so the submission banner can take over the scroll.
      else setPendingErrorField(null);
    };
    frame = window.requestAnimationFrame(locate);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
    };
  }, [pendingErrorField, step, resolvedStepDynamicFields]);

  useEffect(() => {
    if (!submissionError || pendingErrorField) return;
    requestAnimationFrame(() => {
      submissionErrorNode.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  }, [submissionError, pendingErrorField]);

  // "Fill from copied data": one clipboard value per fillable visible field of this step, top to bottom.
  const clipboardFill = activeStepDefinition?.clipboard_fill;
  const fillFromClipboard = async () => {
    let text = '';
    try {
      text = await navigator.clipboard.readText();
    } catch {
      toast.error('Could not read the clipboard. Allow clipboard access for this site (it needs https or localhost) and try again.');
      return;
    }
    if (!text.trim()) { toast.error('The clipboard is empty. Copy the values first, then try again.'); return; }

    const plan = planClipboardFill(
      parseClipboard(text),
      visibleValidationFields,
      (field) => resolvedOptions[field.key] ?? (field.data_source ? undefined : field.options),
      (field) => !readOnly && fieldVisibility[field.key] !== false && !(field.disabled_when && matchesCondition(field.disabled_when, values, fieldVisibility)),
    );
    plan.assignments.forEach((assignment) => setValue(assignment.key, assignment.value));
    setErrors((current) => {
      const next = { ...current };
      plan.assignments.forEach((assignment) => { delete next[assignment.key]; });
      plan.unmatched.forEach((item) => { next[item.key] = item.reason; });
      return next;
    });

    const summary = `Filled ${plan.assignments.length} of ${plan.fieldCount} fields.`;
    const notes = [
      plan.unmatched.length ? `${plan.unmatched.length} value${plan.unmatched.length === 1 ? '' : 's'} did not fit: ${plan.unmatched.map((item) => item.label).join(', ')}.` : '',
      plan.extraValues ? `${plan.extraValues} extra value${plan.extraValues === 1 ? ' was' : 's were'} ignored.` : '',
    ].filter(Boolean);
    if (!plan.assignments.length) toast.error([summary, ...notes].join(' '));
    else if (notes.length) toast(`${summary} ${notes.join(' ')}`, { icon: '⚠️' });
    else toast.success(summary);
  };

  const next = () => {
    const invalid = validate(visibleValidationFields);
    if (Object.keys(invalid).length) { focusFirstError(invalid); return; }
    if (step === schema.steps.length - 1) {
      const allInvalid = validate(allVisibleFields);
      if (Object.keys(allInvalid).length) { focusFirstError(allInvalid); return; }
    }
    changeStep((s) => Math.min(s + 1, reviewStepIndex));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!submitIntentRef.current || submitting) return;
    submitIntentRef.current = false;

    const invalid = validate(allVisibleFields);
    if (Object.keys(invalid).length) {
      const firstStep = schema.steps.findIndex((s) => s.fields.some((f) => invalid[f.key] || (f.type === 'dynamic_group' && (resolvedDynamicFields[f.key] || []).some((child) => invalid[child.key]))) || (resolvedStepDynamicFields[s.id] || []).some((child) => invalid[child.key]));
      if (firstStep >= 0) changeStep(firstStep);
      focusFirstError(invalid);
      return;
    }
    if (onSubmit) {
      setSubmitting(true);
      try { await onSubmit(values); } finally { setSubmitting(false); }
      return;
    }
    if (runtimeFormId) {
      try {
        setSubmitting(true);
        setSubmissionError(null);
        setIsSubmissionErrorModalOpen(false);
        const result = await formBuilderApi.submit(runtimeFormId, values, schema.submission, { calibrationType });
        if (result.reset_form) setValues({});
        toast.success(result.message || 'Form submitted successfully.');
      } catch (error: any) {
        const detail = error?.response?.data?.detail;
        const fieldErrors = Array.isArray(detail)
          ? detail
          : Array.isArray(detail?.errors)
            ? detail.errors
            : Array.isArray(detail?.detail)
              ? detail.detail
              : [];
        const configuredMappings = schema.submission?.error_mappings || [];
        const knownFieldKeys = new Set<string>([
          ...allFields.map((f) => f.key),
          ...Object.values(resolvedDynamicFields).flat().map((f) => f.key),
          ...Object.values(resolvedStepDynamicFields).flat().map((f) => f.key),
        ]);

        const mapped: Record<string, string> = {};
        fieldErrors.forEach((item: any) => {
          const itemField = getApiErrorField(item);
          const itemCode = getApiErrorCode(item);
          const mapping = configuredMappings.find((candidate) => {
            if (!candidate.code) return false;
            if (String(candidate.code) !== String(itemCode ?? '')) return false;
            return !candidate.field || !itemField || candidate.field === itemField;
          });
          const field = mapping?.field || itemField;
          // The API names fields in its own vocabulary (e.g. "certificate"). Only attach the
          // error to the form when it matches a field that exists here; otherwise it is a
          // submission-level error shown in the banner, not a per-field validation error.
          if (field && knownFieldKeys.has(field)) {
            mapped[field] = mapping?.message || item.message || item.msg || 'Submission failed';
          }
        });

        if (Object.keys(mapped).length) {
          setErrors((current) => ({ ...current, ...mapped }));
        }

        const firstFieldError = Object.keys(mapped)[0];
        if (firstFieldError) {
          const firstStep = schema.steps.findIndex((s) =>
            s.fields.some((f) => f.key === firstFieldError) ||
            (resolvedStepDynamicFields[s.id] || []).some((f) => f.key === firstFieldError),
          );
          if (firstStep >= 0 && firstStep !== step) changeStep(firstStep);
          setPendingErrorField(firstFieldError);
        }

        const detailCode = detail?.code ?? detail?.error_code ?? getApiErrorCode(fieldErrors.find((item: any) => getApiErrorCode(item) != null));
        const formMapping = configuredMappings.find((mapping) => {
          if (mapping.field || !mapping.code) return false;
          const responseCodes = [
            detailCode,
            ...fieldErrors.map((item: any) => item?.code ?? item?.error_code),
          ].filter((code) => code != null);
          return responseCodes.some((code) => String(mapping.code) === String(code));
        });
        const message = formMapping?.message || formatSubmissionError(detail ?? error?.message);
        setSubmissionError(message);
        setIsSubmissionErrorModalOpen(false);
      } finally {
        setSubmitting(false);
      }
    }
  };


  return (
    <form className="fb-runtime mx-auto w-full max-w-3xl text-slate-800" onSubmit={submit} noValidate>
      {totalStepCount > 1 && (
        <div data-tour="runtime-steps" className="-mx-2 sticky top-0 z-20 mb-6 overflow-x-auto bg-white/95 px-2 pb-2 pt-1 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-white/80" aria-label="Form steps">
          <div className="flex min-w-max items-center gap-1">
            {schema.steps.map((s, i) => (
              <button type="button" key={s.id} className={`flex shrink-0 items-center min-w-40 gap-2 rounded-lg px-3 py-2 text-left transition ${i === step ? 'bg-primary-50 text-primary-700' : i < step ? 'text-slate-500 hover:bg-slate-50' : 'text-slate-400 hover:bg-slate-50'}`} onClick={() => { changeStep(i); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                <span className={`grid h-6 w-6 place-items-center rounded-md text-[10px] font-semibold ${i === step ? 'bg-primary-600 text-white' : i < step ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>{i < step ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
                <span className="max-w-[160px] truncate text-[11px] font-medium">{s.title}</span>
              </button>
            ))}
            <button type="button" className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left transition ${onReviewStep ? 'bg-primary-50 text-primary-700' : 'text-slate-400 hover:bg-slate-50'}`} onClick={() => changeStep(reviewStepIndex)}>
              <span className={`grid h-6 w-6 place-items-center rounded-md text-[10px] font-semibold ${onReviewStep ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-400'}`}>{schema.steps.length + 1}</span>
              <span className="max-w-[160px] truncate text-[11px] font-medium">{schema.review?.title || 'Review & submit'}</span>
            </button>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-primary-600">Step {step + 1} of {totalStepCount}</div>
        <h2 className="mt-1 text-xl font-semibold tracking-tight text-slate-900">
          {onReviewStep ? (schema.review?.title || 'Review & submit') : schema.steps[step]?.title}
        </h2>
        {(onReviewStep ? schema.review?.description : schema.steps[step]?.description) && (
          <p className="mt-1.5 max-w-2xl text-[12px] leading-5 text-slate-500">{onReviewStep ? schema.review?.description : schema.steps[step]?.description}</p>
        )}
      </div>


      {submissionError && (
        <SubmissionErrorBanner
          containerRef={submissionErrorNode}
          message={submissionError}
          modalOpen={isSubmissionErrorModalOpen}
          onOpenModal={() => setIsSubmissionErrorModalOpen(true)}
          onCloseModal={() => setIsSubmissionErrorModalOpen(false)}
          onDismiss={() => setSubmissionError(null)}
        />
      )}

      {Object.keys(errors).length > 0 && (
        <div className="mb-5 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[11px] text-red-700" role="alert">
          <div className="flex items-center gap-2"><AlertCircle className="h-4 w-4 shrink-0" /><strong className="font-semibold">Please fix {Object.keys(errors).length} {Object.keys(errors).length === 1 ? 'error' : 'errors'} before continuing.</strong></div>
          <button type="button" className="shrink-0 font-semibold underline underline-offset-2" onClick={() => focusFirstError(errors)}>Go to first error</button>
        </div>
      )}

      {onReviewStep ? (
        <ReviewView groups={reviewFieldsByStep} values={values} resolvedOptions={resolvedOptions} />
      ) : (
        <div data-tour="runtime-fields">
          {clipboardFill?.enabled && !readOnly && (
            <div className="mb-4 flex justify-end">
              <button type="button" onClick={fillFromClipboard} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-[12px] font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50">
                <ClipboardPaste className="h-3.5 w-3.5" /> {clipboardFill.label?.trim() || DEFAULT_CLIPBOARD_FILL_LABEL}
              </button>
            </div>
          )}
          {stepDynamicLoading[activeStepDefinition?.id || ''] && (
            <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] text-slate-500">Loading runtime fields…</div>
          )}
          {stepDynamicErrors[activeStepDefinition?.id || ''] && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[11px] text-red-700">{stepDynamicErrors[activeStepDefinition?.id || '']}</div>
          )}
          {visibleFields.map((f) => <FieldControl key={f.key} field={f} values={values} setValue={setValue} error={errors[f.key]} readOnly={readOnly || lockFirstStep} runtimeFormId={runtimeFormId} dataSourceDraft={dataSourceDraft} fieldRef={(node) => { fieldNodes.current[f.key] = node; }}
          onOptions={(options) => setResolvedOptions((current) => ({ ...current, [f.key]: options }))}
          onDynamicFields={(fields) => handleDynamicFieldsChange(f.key, fields)}
          cachedDynamicFields={resolvedDynamicFields[f.key] || []}
          fieldErrors={errors} visibleKeys={visibleKeys} fieldVisibility={fieldVisibility} registerFieldRef={(key, node) => { fieldNodes.current[key] = node; }} />)}</div>
      )}

      {showStepNavigation && (
        <div data-tour="runtime-nav" className="mt-8 flex items-center justify-between border-t border-slate-100 pt-4">
          <button className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2.5 text-[12px] font-medium text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 disabled:opacity-40" type="button" disabled={step === 0} onClick={() => { changeStep((s) => s - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <ChevronLeft className="h-4 w-4" /> Back
          </button>
          {!onReviewStep && step < schema.steps.length - 1
            ? <button className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-4 text-[12px] font-semibold text-white shadow-sm transition hover:bg-slate-800" type="button" onClick={next}>Next <ChevronRight className="h-3.5 w-3.5" /></button>
            : !onReviewStep
              ? <button className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-slate-900 px-4 text-[12px] font-semibold text-white shadow-sm transition hover:bg-slate-800" type="button" onClick={next}>Review <ChevronRight className="h-3.5 w-3.5" /></button>
              : <button className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-0 text-[12px] font-semibold text-white shadow-sm transition hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60" type="submit" disabled={submitting} aria-busy={submitting} onClick={() => { submitIntentRef.current = true; }}>{schema.submit_label || 'Submit'} {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronRight className="h-3.5 w-3.5" />}</button>}
        </div>
      )}
    </form>
  );
}
